import { useEffect, useState } from 'react'
import { ChangeEvent } from 'react'

import { Container, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@mui/material/useMediaQuery'

import TextArea from '@/components/atoms/TextArea'
import Button from '@/components/atoms/Button'
import ModalHeader from '@/components/molecules/ModalHeader'
import ArrowIcon from 'public/images/next-arrow.svg'
import RefreshIcon from 'public/images/refresh.svg'
import { useGetWishSuggestions } from '@/services/wish-suggestion/hooks'
import { getWishStorage, setWishStorage } from '@/utils/index'

import ComponentStyled from './styled'

const CHARACTERS_LIMIT = 80

type Props = {
  onConfirm: (text: string) => void
}

const CreateWish = ({ onConfirm }: Props): JSX.Element => {
  const { data: inspires } = useGetWishSuggestions()
  const { t } = useTranslation()
  const [wish, setWish] = useState('')
  const characters = wish?.length
  const [animateRefreshIcon, setAnimateRefreshIcon] = useState(false)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const handleChangeWish = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    if (text.length <= CHARACTERS_LIMIT) {
      setWishStorage(text)
      setWish(text)
    }
  }

  const handleClickInspire = () => {
    const random = Math.floor(Math.random() * inspires.length)
    const wishSuggestionName = inspires[random].attributes.name
    setWish(wishSuggestionName)
    setAnimateRefreshIcon(true)
  }

  useEffect(() => {
    const wishStorage = getWishStorage()
    if (wishStorage) {
      setWish(wishStorage)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setAnimateRefreshIcon(false), 300)
    return () => clearTimeout(timer)
  }, [animateRefreshIcon])

  const handleConfirmWish = () => {
    onConfirm(wish)
  }

  return (
    <ComponentStyled animateRefreshIcon={animateRefreshIcon}>
      <Container maxWidth='xl'>
        <div className='create-wish-box'>
          <ModalHeader
            prepend={t('Step 2')}
            title={t('Share your wish')}
            description={t('Leave your wish for a better future')}
          />
          <div className='create-wish-content'>
            <div className='write-wish-wrapper'>
              <p className='characters'>
                {characters}/{CHARACTERS_LIMIT}
              </p>
              <TextArea
                placeholder={t('What‘s your personal wish or commitment for a better future?')}
                value={wish}
                onChange={handleChangeWish}
              />
              {inspires.length > 0 && (
                <p className='inspire' onClick={handleClickInspire}>
                  {t('Inspire me!')}
                  <RefreshIcon className='refresh-icon' />
                </p>
              )}
            </div>

            <div className='next-btn-wrapper'>
              <Button
                fitToContainer={isMobile}
                disabled={!wish.length}
                size='medium'
                label={t('Next step')}
                icon={<ArrowIcon />}
                onClick={handleConfirmWish}
              />
            </div>
          </div>
        </div>
      </Container>
    </ComponentStyled>
  )
}

export default CreateWish
